<template>
  <HirePageHeaderAlerts :hire-id="hireId" />
  <PageHeader>
    <template #header-breadcrumbs>
      <WdsStack direction-row justify-space-between class="tw-w-full tw-mb-4">
        <WdsButtonGhost
          :href="laravelNamedRoute(namedRoutes.frontend.profiles.index.engaged)"
          secondary
        >
          <WdsStack align-center gap-sm>
            <WdsIcon name="arrow-left" :size="16" />
            {{ $t('Hires') }}
          </WdsStack>
        </WdsButtonGhost>
        <WdsStack gap-sm>
          <WdsButton
            v-if="hire?.conversation?.url"
            :href="`${hire?.conversation?.url}?managecontract=1`"
          >
            {{ $t('Manage contract') }}
          </WdsButton>
          <!--        <WdsButton @click="openContractDetailPanels">-->
          <!--          {{ $t('Manage contract') }}-->
          <!--        </WdsButton>-->

          <WdsButton v-if="hire?.conversation?.url" primary :href="hire?.conversation?.url">
            {{ $t('Message') }}
          </WdsButton>
        </WdsStack>
      </WdsStack>
    </template>
    <template #header-title>
      <div class="tw-line-clamp-2">
        {{ hire?.job?.name ?? $t('Hire overview') }}
      </div>
    </template>
    <template #header-description>
      <WdsStack v-if="loading" align-start></WdsStack>

      <WdsStack v-else-if="hire" justify-start gap-custom="48px">
        <!--   Hire / job information   -->
        <WdsStack direction-column gap-xs>
          <WdsText secondary>
            <WdsStack gap-sm align-center>
              <span>{{ $t('Hire ID') }}: #{{ hire.number }}</span>
              <WdsLabel
                :gray="hire.activeStatus === HireActiveStatus.Draft"
                :success="hire.activeStatus === HireActiveStatus.Active"
                :warning="hire.activeStatus === HireActiveStatus.Ended"
                :primary="
                  [HireActiveStatus.Signed, HireActiveStatus.Offered].includes(hire.activeStatus)
                "
                :error="
                  [HireActiveStatus.Cancelled, HireActiveStatus.Terminated].includes(
                    hire.activeStatus,
                  )
                "
                subtle
                xs
              >
                {{ convertEnumToDisplayText(hire.activeStatus) }}
              </WdsLabel>
            </WdsStack>
          </WdsText>
          <WdsText>{{ timeLine.start }} - {{ timeLine.end }}</WdsText>
          <WdsText secondary>
            <WdsStack justify-start align-center gap-xs class="hire-job-owners">
              {{ $t('Owners') }}:
              <WdsText
                v-for="(owner, index) in jobOwnersSliced"
                :key="owner.id"
                class="tw-text-nowrap"
              >
                <span v-if="index < jobOwnersSliced.length - 1">{{ owner.name }},</span>
                <span v-else>{{ owner.name }}</span>
              </WdsText>
              <WdsButtonGhost
                v-if="jobOwners.length > jobOwnersSliced.length"
                @click="sliceVal = 100000"
              >
                <WdsLabel :title="$t('See all job owners')" xs subtle>
                  +{{ jobOwners.length - jobOwnersSliced.length }}
                </WdsLabel>
              </WdsButtonGhost>
            </WdsStack>
          </WdsText>
        </WdsStack>
        <div
          class="tw-self-stretch tw-bg-gray-100 tw-inline-flex tw-justify-start tw-items-center tw-gap-2.5"
        >
          <div class="tw-w-[1px] tw-self-stretch tw-border-r tw-border-gray-400"></div>
        </div>
        <!-- User Details Section -->
        <WdsButtonGhost class="tw-p-1" @click="openWorkerDetailPanel">
          <WdsStack align-center gap-md>
            <WdsAvatarLabel>
              <template #avatar>
                <div class="tw-relative">
                  <WdsAvatar
                    v-if="hire.recruiter"
                    class="tw-absolute tw-top-[-14px] tw-left-[-14px]"
                    :path="getAvatarPath(hire.recruiter.avatar, hire.recruiter.id)"
                  />
                  <WdsAvatar :path="getAvatarPath(hire.worker.avatar, hire.worker.id)" xl />
                </div>
              </template>

              <WdsStack gap-xs justify-start align-center>
                <WdsText :text="hire.worker.name" xl />
                <WdsIcon v-if="hire.recruiter" name="group" :size="14" />
              </WdsStack>
              <WdsStack direction-column gap-xs align-start>
                <WdsText
                  v-if="hire.worker.jobTitle"
                  sm
                  secondary
                  class="tw-text-left tw-line-clamp-2"
                >
                  {{ hire.worker.jobTitle }}
                </WdsText>
                <div v-if="hire.recruiter">
                  <WdsTag sm>{{ hire.recruiter.name }}</WdsTag>
                </div>
              </WdsStack>
            </WdsAvatarLabel>
            <WdsIcon name="chevron-right" :size="16" />
          </WdsStack>
        </WdsButtonGhost>
      </WdsStack>
    </template>
  </PageHeader>

  <WdsDrawer v-model:show="showWorkerDetailPanel" position="right" width="420px" padding="0">
    <WorkerDetailPanel
      v-if="hire"
      :worker-id="hire.worker.id"
      :viewer-account-id="hire.company.id"
      @close="showWorkerDetailPanel = false"
    />
  </WdsDrawer>

  <WdsDrawer v-model:show="showContractDetailsPanel" position="right" width="650px" padding="0">
    <ContractDetailsPanel :hire-id="hireId" @close="showContractDetailsPanel = false" />
  </WdsDrawer>
</template>

<script setup lang="ts">
import { computed, ref } from 'vue'
import { useQuery } from '@vue/apollo-composable'
import {
  WdsAvatar,
  WdsAvatarLabel,
  WdsButton,
  WdsButtonGhost,
  WdsDrawer,
  WdsIcon,
  WdsLabel,
  WdsStack,
  WdsTag,
  WdsText,
} from '@worksome/wds-components'

import { graphql, useFragment } from '@/gql'
import { $t } from '@/app-bootstrapping/SetUpInternationalization'
import useDate from '@/composables/useDate'
import { getAvatarPath } from '@/utils/avatar'
import { convertEnumToDisplayText } from '@/utils/tools'
import { laravelNamedRoute } from '@/utils/laravelNamedRoutes'
import namedRoutes from '@/utils/laravelNamedRoutes.json'
import HirePageHeaderAlerts from '@/components/Hire/HirePageHeaderAlerts.vue'
import { HireActiveStatus } from '@/gql/graphql'

import WorkerDetailPanel from '@/components/WorkerDetailPanel/WorkerDetailPanel.vue'
import ContractDetailsPanel from '@/components/ContractDetailsPanel/ContractDetailsPanel.vue'
import PageHeader from '@/components/PageHeader.vue'

const props = defineProps<{
  hireId: string
}>()

const ContractFragment = graphql(`
  fragment ContractFields on Contract {
    id
    status
    jobName
    startDate
    endDate
  }
`)

const HireFragment = graphql(`
  fragment HireHeadingFields on Hire {
    id
    number
    activeStatus
    activeContract {
      ...ContractFields
    }
    latestContract {
      ...ContractFields
    }
    conversation {
      id
      url
    }
    job {
      id
      name
      owners {
        id
        name
      }
    }
    worker {
      id
      name
      jobTitle
      avatar
    }
    company {
      id
      name
    }
    recruiter {
      id
      name
      avatar
    }
  }
`)

const GET_HIRE = graphql(`
  query GetHireForHeader($id: ID!) {
    hire(id: $id) {
      ...HireHeadingFields
    }
  }
`)

const { result, loading } = useQuery(GET_HIRE, () => ({ id: props.hireId }), {
  clientId: 'publicApi',
})

const sliceVal = ref(2)
const showWorkerDetailPanel = ref(false)
const showContractDetailsPanel = ref(false)

const hire = computed(() => useFragment(HireFragment, result.value?.hire))
const contract = computed(() =>
  hire.value !== null && hire.value !== undefined
    ? useFragment(ContractFragment, hire.value.activeContract ?? hire.value.latestContract)
    : null,
)

const timeLine = computed(() => {
  if (contract.value === null) {
    return { start: '', end: '' }
  }

  const { date: start } = useDate(contract.value.startDate ?? '')
  const { date: end } = useDate(contract.value.endDate ?? '')

  return { start, end }
})

const jobOwners = computed(() => hire.value?.job?.owners ?? [])
const jobOwnersSliced = computed(() => jobOwners.value.slice(0, sliceVal.value))

const openWorkerDetailPanel = () => {
  showContractDetailsPanel.value = false
  showWorkerDetailPanel.value = true
}

const openContractDetailPanels = () => {
  showWorkerDetailPanel.value = false
  showContractDetailsPanel.value = true
}
</script>

<style lang="scss" scoped>
.hire-job-owners {
  &:deep(.wds-button-ghost) {
    padding: 0;
  }
}
</style>
